import { useState } from "react"
import { useDraggable } from "@dnd-kit/core"
import { ChevronDownIcon, LanguagesIcon, MessageSquarePlusIcon, PlusIcon, SaveIcon, SettingsIcon } from "lucide-react"
import { EditableLabel } from "@/components/editable-text/editable-label"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"
import { IssuesButton } from "./issues"
import { languageName } from "./language"
import { PlaybookSettingsDialog } from "./playbooksettingsdialog"
import { flowTitle, slugify } from "./text"

interface Props {
  playbook: Playbook
  flow: string
  issues: PlaybookIssue[]
  onChange: (playbook: Playbook) => void
  onSelectFlow: (flow: string) => void
  onAppend: (kind: "item" | "ai") => void
  language?: string
  onLanguage: (language?: string) => void
  onSave?: () => void
}

export function PlaybookHeader({ playbook, flow, issues, onChange, onSelectFlow, onAppend, language, onLanguage, onSave }: Props) {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState("")
  const key = slugify(name)
  const taken = key === "main" || !!playbook.flows[key]
  const names = ["main", ...Object.keys(playbook.flows).filter(name => name !== "main")]
  const defaultLanguage = playbook.config?.language || "en"

  function openAdd() {
    setName("")
    setAdding(true)
  }

  function acceptName() {
    if (!name.trim() || taken) return
    onChange({ ...playbook, flows: { ...playbook.flows, [key]: { title: name.trim(), items: [] } } })
    onSelectFlow(key)
    setAdding(false)
  }

  return <div className="flex items-center gap-2 pb-2 shrink-0">
    <EditableLabel value={playbook.title ?? ""} onChange={title => onChange({ ...playbook, title })} placeholder="Untitled playbook" className="block w-1/2 truncate text-2xl font-semibold" />
    <div className="flex-1" />
    <div className="flex items-center gap-1">
      <AddItemHandle kind="ai" onClick={() => onAppend("ai")} />
      <AddItemHandle kind="item" onClick={() => onAppend("item")} />
      <Separator orientation="vertical" className="mx-1 h-5 self-center!" />
      <span className="pl-1 text-sm font-medium text-muted-foreground">Playbook</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="w-56 justify-between font-normal">
            <span className="truncate">{flowTitle(playbook.flows, flow)}</span>
            <ChevronDownIcon className="opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          {names.map(name => <DropdownMenuItem key={name} onSelect={() => onSelectFlow(name)} className={cn(name === flow && "font-medium bg-muted/50")}>
            <span className="truncate">{flowTitle(playbook.flows, name)}</span>
          </DropdownMenuItem>)}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={openAdd}><PlusIcon />Add flow</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Separator orientation="vertical" className="mx-1 h-5 self-center!" />
      {!!issues.length && <>
        <IssuesButton issues={issues} flows={playbook.flows} onSelectFlow={onSelectFlow} onOpenSettings={() => setSettingsOpen(true)} />
        <Separator orientation="vertical" className="mx-1 h-5 self-center!" />
      </>}
      {language && <span className="pl-1 text-sm font-medium text-primary">{languageName(language)}</span>}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" aria-label="Display language" title="Display language" className="gap-0.5 px-1.5"><LanguagesIcon /><ChevronDownIcon className="size-3 opacity-50" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuRadioGroup value={language ?? ""} onValueChange={value => onLanguage(value || undefined)}>
            <DropdownMenuRadioItem value="">Default ({defaultLanguage})</DropdownMenuRadioItem>
            {playbook.config?.languages?.map(code => <DropdownMenuRadioItem key={code} value={code}>{languageName(code)}</DropdownMenuRadioItem>)}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" aria-label="Save and deploy" title="Save and deploy" className="gap-0.5 px-1.5"><SaveIcon /><ChevronDownIcon className="size-3 opacity-50" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={onSave} disabled={!onSave}>Save</DropdownMenuItem>
          <DropdownMenuItem disabled>Deploy to production</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button variant="ghost" size="icon-sm" aria-label="Playbook settings" title="Playbook settings" onClick={() => setSettingsOpen(true)}><SettingsIcon /></Button>
    </div>

    <PlaybookSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} playbook={playbook} onChange={onChange} />

    <Dialog open={adding} onOpenChange={setAdding}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add flow</DialogTitle>
        </DialogHeader>
        <form onSubmit={e => { e.preventDefault(); acceptName() }} className="flex flex-col gap-2">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Flow name" autoFocus />
          {name.trim() && <p className="text-xs text-muted-foreground">{taken ? `"${key}" is already a flow` : `Stored as "${key}"`}</p>}
          <DialogFooter>
            <Button type="submit" disabled={!name.trim() || taken}>Add</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

  </div>
}

function AddItemHandle({ kind, onClick }: { kind: "item" | "ai", onClick: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `new-${kind}` })
  return <Button
    ref={setNodeRef}
    {...attributes}
    {...listeners}
    onClick={onClick}
    variant="ghost"
    size="icon-sm"
    aria-label={kind === "ai" ? "Add AI item" : "Add item"}
    className={cn("cursor-grab touch-none", isDragging && "opacity-50")}
  >
    {kind === "ai" ? <span className="text-base leading-none">🧠</span> : <MessageSquarePlusIcon />}
  </Button>
}
